import { useState } from 'react';
import {
  Plus,
  FlaskConical,
  Pencil,
  Trash2,
  Play,
  Eye,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  X,
  Copy,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useSimulations, useSimulationCalculation, useSimulationComparison } from '@hooks/useSimulations';
import { useCategories } from '@hooks/useCategories';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import DatePicker from '@components/common/DatePicker';
import Select from '@components/common/Select';
import Badge from '@components/common/Badge';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import { formatCurrency } from '@utils/formatters';
import { clsx } from 'clsx';

const COLORS = ['#6366F1', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const Simulation = () => {
  const {
    simulations,
    isLoading,
    createSimulation,
    updateSimulation,
    deleteSimulation,
    addEvent,
    removeEvent,
    isCreating,
  } = useSimulations();

  const { categories } = useCategories();

  const [activeSimulationId, setActiveSimulationId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [isSimulationModalOpen, setIsSimulationModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [viewMode, setViewMode] = useState('single'); // 'single' | 'compare'

  // Buscar cálculo da simulação ativa
  const { data: calculationData, isLoading: isCalculating } = useSimulationCalculation(activeSimulationId);

  // Buscar comparação
  const { data: comparisonData, isLoading: isComparing } = useSimulationComparison(compareIds);

  // Form states
  const [simulationForm, setSimulationForm] = useState({
    name: '',
    description: '',
    horizonMonths: 12,
    icon: '🔮',
    color: '#6366F1',
  });

  const [eventForm, setEventForm] = useState({
    type: 'expense',
    description: '',
    amount: '',
    startDate: new Date().toISOString().split('T')[0],
    frequency: 'once',
    installments: '',
    categoryId: '',
  });

  const handleCreateSimulation = async () => {
    await createSimulation(simulationForm);
    setIsSimulationModalOpen(false);
    setSimulationForm({ name: '', description: '', horizonMonths: 12, icon: '🔮', color: '#6366F1' });
  };

  const handleAddEvent = async () => {
    if (!activeSimulationId) return;
    
    const eventData = {
      ...eventForm,
      amount: eventForm.type === 'expense' ? -Math.abs(eventForm.amount || 0) : Math.abs(eventForm.amount || 0),
      installments: eventForm.installments ? parseInt(eventForm.installments) : undefined,
    };

    await addEvent({ simulationId: activeSimulationId, event: eventData });
    setIsEventModalOpen(false);
    setEventForm({
      type: 'expense',
      description: '',
      amount: '',
      startDate: new Date().toISOString().split('T')[0],
      frequency: 'once',
      installments: '',
      categoryId: '',
    });
  };

  const handleDeleteEvent = async (eventId) => {
    if (!activeSimulationId) return;
    await removeEvent({ simulationId: activeSimulationId, eventId });
  };

  const handleSelectSimulation = (simulation) => {
    setActiveSimulationId(simulation.simulationId);
    setSelectedSimulation(simulation);
    setViewMode('single');
  };

  const toggleCompareSimulation = (simulationId) => {
    setCompareIds(prev => {
      if (prev.includes(simulationId)) {
        return prev.filter(id => id !== simulationId);
      }
      return [...prev, simulationId];
    });
    setViewMode('compare');
  };

  if (isLoading) {
    return <Loading text="Carregando simulações..." />;
  }

  const activeSimulation = simulations.find(s => s.simulationId === activeSimulationId);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Simulação Financeira"
        subtitle="Crie cenários e visualize o impacto nas suas finanças"
        action={
          <Button leftIcon={<Plus size={18} />} onClick={() => setIsSimulationModalOpen(true)}>
            Nova Simulação
          </Button>
        }
      />

      <PageTutorial
        pageId="simulation"
        title="Simulador financeiro"
        steps={[
          'Crie cenarios hipoteticos para planejar decisoes financeiras',
          'Adicione eventos como aumentos, cortes ou novos gastos ao cenario',
          'Compare ate 3 cenarios lado a lado para escolher a melhor opcao',
        ]}
      />

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar - Lista de Simulações */}
        <div className="lg:col-span-1 space-y-4">
          <Card title="📁 Cenários" padding="sm">
            {simulations.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <FlaskConical size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma simulação</p>
              </div>
            ) : (
              <div className="space-y-2">
                {simulations.map((sim) => (
                  <div
                    key={sim.simulationId}
                    className={clsx(
                      'p-3 rounded-lg border-2 cursor-pointer transition-all',
                      activeSimulationId === sim.simulationId
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'
                    )}
                    onClick={() => handleSelectSimulation(sim)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{sim.icon}</span>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            {sim.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {sim.events?.length || 0} eventos • {sim.horizonMonths} meses
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={compareIds.includes(sim.simulationId)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleCompareSimulation(sim.simulationId);
                        }}
                        className="w-4 h-4 text-primary-600 rounded"
                        title="Incluir na comparação"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {compareIds.length > 0 && (
            <Button
              variant="secondary"
              fullWidth
              leftIcon={<Eye size={18} />}
              onClick={() => setViewMode('compare')}
            >
              Comparar ({compareIds.length + 1})
            </Button>
          )}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {!activeSimulationId && viewMode === 'single' ? (
            <EmptyState
              icon={<FlaskConical size={48} />}
              title="Selecione uma simulação"
              description="Escolha uma simulação na lista ao lado ou crie uma nova para começar"
              action={() => setIsSimulationModalOpen(true)}
              actionLabel="Criar Simulação"
            />
          ) : viewMode === 'compare' && compareIds.length > 0 ? (
            // Modo Comparação
            <ComparisonView
              data={comparisonData}
              isLoading={isComparing}
              onClose={() => {
                setViewMode('single');
                setCompareIds([]);
              }}
            />
          ) : (
            // Modo Single
            <>
              {/* Header da Simulação */}
              <Card>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{activeSimulation?.icon}</span>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {activeSimulation?.name}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {activeSimulation?.description || 'Sem descrição'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Plus size={16} />}
                      onClick={() => setIsEventModalOpen(true)}
                    >
                      Adicionar Evento
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      leftIcon={<Trash2 size={16} />}
                      onClick={() => deleteSimulation(activeSimulationId)}
                    >
                      Excluir
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Eventos da Simulação */}
              <Card title="📋 Eventos Simulados">
                {activeSimulation?.events?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>Nenhum evento adicionado</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={() => setIsEventModalOpen(true)}
                    >
                      Adicionar primeiro evento
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {activeSimulation?.events?.map((event) => (
                      <div
                        key={event.eventId}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            event.amount > 0
                              ? 'bg-green-100 dark:bg-green-900/30'
                              : 'bg-red-100 dark:bg-red-900/30'
                          )}>
                            {event.amount > 0 ? (
                              <TrendingUp className="text-green-600" size={20} />
                            ) : (
                              <TrendingDown className="text-red-600" size={20} />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {event.description}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{event.startDate}</span>
                              <span>•</span>
                              <span>
                                {event.frequency === 'once' ? 'Único' : 
                                 event.installments ? `${event.installments}x` : event.frequency}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={clsx(
                            'font-semibold',
                            event.amount > 0 ? 'text-green-600' : 'text-red-600'
                          )}>
                            {formatCurrency(event.amount)}
                          </span>
                          <button
                            onClick={() => handleDeleteEvent(event.eventId)}
                            className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20"
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Resultado da Simulação */}
              {isCalculating ? (
                <Loading text="Calculando simulação..." />
              ) : calculationData ? (
                <SimulationResult data={calculationData} />
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Modal Nova Simulação */}
      <Modal
        isOpen={isSimulationModalOpen}
        onClose={() => setIsSimulationModalOpen(false)}
        title="Nova Simulação"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsSimulationModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateSimulation} isLoading={isCreating}>
              Criar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome do Cenário"
            placeholder="Ex: Comprar Carro, Mudar de Emprego..."
            value={simulationForm.name}
            onChange={(e) => setSimulationForm({ ...simulationForm, name: e.target.value })}
          />
          <Input
            label="Descrição (opcional)"
            placeholder="Descreva o cenário..."
            value={simulationForm.description}
            onChange={(e) => setSimulationForm({ ...simulationForm, description: e.target.value })}
          />
          <Input
            label="Horizonte (meses)"
            type="number"
            min="1"
            max="60"
            value={simulationForm.horizonMonths}
            onChange={(e) => setSimulationForm({ ...simulationForm, horizonMonths: parseInt(e.target.value) })}
          />
        </div>
      </Modal>

      {/* Modal Adicionar Evento */}
      <Modal
        isOpen={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
        title="Adicionar Evento"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsEventModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddEvent}>
              Adicionar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium mb-2">Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setEventForm({ ...eventForm, type: 'expense' })}
                className={clsx(
                  'py-3 px-4 rounded-lg border-2 font-medium transition-all',
                  eventForm.type === 'expense'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 text-gray-600'
                )}
              >
                💸 Despesa
              </button>
              <button
                type="button"
                onClick={() => setEventForm({ ...eventForm, type: 'income' })}
                className={clsx(
                  'py-3 px-4 rounded-lg border-2 font-medium transition-all',
                  eventForm.type === 'income'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600'
                )}
              >
                💰 Receita
              </button>
            </div>
          </div>

          <Input
            label="Descrição"
            placeholder="Ex: Financiamento do carro..."
            value={eventForm.description}
            onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
          />

          <div className="grid grid-cols-2 gap-4">
            <CurrencyInput
              label="Valor"
              value={eventForm.amount}
              onValueChange={(values) => setEventForm({ ...eventForm, amount: values.floatValue ?? '' })}
            />
            <DatePicker
              label="Data de Início"
              value={eventForm.startDate}
              onChange={(e) => setEventForm({ ...eventForm, startDate: e.target.value })}
            />
          </div>

          <Select
            label="Frequência"
            options={[
              { value: 'once', label: 'Pagamento Único' },
              { value: 'monthly', label: 'Mensal (Recorrente)' },
              { value: 'weekly', label: 'Semanal' },
              { value: 'yearly', label: 'Anual' },
            ]}
            value={eventForm.frequency}
            onChange={(e) => setEventForm({ ...eventForm, frequency: e.target.value })}
          />

          {eventForm.frequency === 'once' && (
            <Input
              label="Número de Parcelas (opcional)"
              type="number"
              min="1"
              max="72"
              placeholder="Ex: 48 para financiamento"
              value={eventForm.installments}
              onChange={(e) => setEventForm({ ...eventForm, installments: e.target.value })}
              hint="Deixe vazio para pagamento à vista"
            />
          )}

          <Select
            label="Categoria (opcional)"
            options={[
              { value: '', label: 'Sem categoria' },
              ...categories.map(c => ({ value: c.categoryId, label: `${c.icon} ${c.name}` })),
            ]}
            value={eventForm.categoryId}
            onChange={(e) => setEventForm({ ...eventForm, categoryId: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
};

// Componente de Resultado da Simulação
const SimulationResult = ({ data }) => {
  const { summary, projection, criticalMonths } = data;

  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="md">
          <p className="text-xs text-gray-500">Saldo Inicial</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(summary.currentBalance)}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-xs text-gray-500">Saldo Final Projetado</p>
          <p className={clsx(
            'text-xl font-bold',
            summary.finalBalance >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {formatCurrency(summary.finalBalance)}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-xs text-gray-500">Impacto Simulado</p>
          <p className={clsx(
            'text-xl font-bold',
            summary.simulatedIncome - summary.simulatedExpense >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {formatCurrency(summary.simulatedIncome - summary.simulatedExpense)}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-xs text-gray-500">Meses Críticos</p>
          <p className={clsx(
            'text-xl font-bold',
            criticalMonths.length > 0 ? 'text-red-600' : 'text-green-600'
          )}>
            {criticalMonths.length}
          </p>
        </Card>
      </div>

      {/* Alertas */}
      {criticalMonths.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-red-800 dark:text-red-300">
                Atenção: Saldo negativo previsto!
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                Os meses {criticalMonths.map(m => m.monthLabel).join(', ')} terão saldo negativo.
                O menor saldo será de {formatCurrency(summary.lowestBalance)}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Gráfico de Projeção */}
      <Card title="📈 Projeção de Saldo">
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={projection}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="monthLabel" stroke="#9ca3af" fontSize={12} />
            <YAxis
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
              stroke="#9ca3af"
              fontSize={12}
            />
            <Tooltip
              formatter={(value) => formatCurrency(value)}
              labelStyle={{ color: '#374151' }}
            />
            <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="5 5" />
            <Line
              type="monotone"
              dataKey="projectedBalance"
              name="Saldo Projetado"
              stroke="#6366F1"
              strokeWidth={3}
              dot={{ fill: '#6366F1', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Detalhamento Mensal */}
      <Card title="📊 Detalhamento Mensal" padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50 border-b">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mês</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Receitas</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Despesas</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo Mês</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo Acumulado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {projection.map((month) => (
                <tr
                  key={month.month}
                  className={clsx(
                    month.isNegative && 'bg-red-50 dark:bg-red-900/10'
                  )}
                >
                  <td className="px-4 py-3 text-sm font-medium">{month.monthLabel}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-600">
                    {formatCurrency(month.income.total)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-red-600">
                    {formatCurrency(month.expense.total)}
                  </td>
                  <td className={clsx(
                    'px-4 py-3 text-sm text-right font-medium',
                    month.monthBalance >= 0 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {formatCurrency(month.monthBalance)}
                  </td>
                  <td className={clsx(
                    'px-4 py-3 text-sm text-right font-bold',
                    month.projectedBalance >= 0 ? 'text-gray-900 dark:text-white' : 'text-red-600'
                  )}>
                    {formatCurrency(month.projectedBalance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// Componente de Comparação
const ComparisonView = ({ data, isLoading, onClose }) => {
  if (isLoading) {
    return <Loading text="Comparando cenários..." />;
  }

  if (!data || data.length === 0) {
    return <EmptyState title="Selecione cenários para comparar" />;
  }

  // Preparar dados para o gráfico
  const chartData = data[0].projection.map((_, index) => {
    const point = { month: data[0].projection[index].monthLabel };
    data.forEach((sim, simIndex) => {
      point[sim.name] = sim.projection[index]?.projectedBalance || 0;
    });
    return point;
  });

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Comparação de Cenários</h2>
          <Button variant="ghost" size="sm" leftIcon={<X size={16} />} onClick={onClose}>
            Fechar
          </Button>
        </div>
      </Card>

      {/* Cards de Resumo por Cenário */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((sim, index) => (
          <Card key={sim.simulationId} padding="md">
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <h3 className="font-semibold">{sim.name}</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Saldo Final</p>
                <p className={clsx(
                  'font-bold',
                  sim.summary.finalBalance >= 0 ? 'text-green-600' : 'text-red-600'
                )}>
                  {formatCurrency(sim.summary.finalBalance)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Menor Saldo</p>
                <p className={clsx(
                  'font-bold',
                  sim.summary.lowestBalance >= 0 ? 'text-gray-900' : 'text-red-600'
                )}>
                  {formatCurrency(sim.summary.lowestBalance)}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Gráfico Comparativo */}
      <Card title="📈 Comparação de Saldos">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
            <YAxis
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
              stroke="#9ca3af"
              fontSize={12}
            />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="5 5" />
            {data.map((sim, index) => (
              <Line
                key={sim.simulationId}
                type="monotone"
                dataKey={sim.name}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

export default Simulation;
